<template>
  <div class="contact-form">
    <!-- Error -->
    <div class="alert alert-danger mt-3" v-if="isError">
      Ladefehler!<br />
      Bitte schreibe uns an {{ config('support.email') }}
    </div>

    <span
      class="float-right ml-4 mr-2 text-muted font-weight-500"
      style="margin-top: -10px"
    >
      Schritt {{ currentStep }}
      <span v-if="currentStep > 1"> /{{ totalSteps }} </span>
    </span>

    <!-- Progress bar -->
    <div class="progress mt-4 mx-2" style="height: 4px">
      <div
        class="progress-bar"
        role="progressbar"
        :style="{ width: (100 / totalSteps) * currentStep + '%' }"
        aria-valuenow="25"
        aria-valuemin="0"
        aria-valuemax="100"
      ></div>
    </div>

    <!-- Categories loader -->
    <div class="text-center mt-4" v-if="isLoading === 'categories'">
      <svg
        class="loader"
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle class="internal-circle" cx="60" cy="60" r="30"></circle>
        <circle class="external-circle" cx="60" cy="60" r="50"></circle>
      </svg>
    </div>

    <!-- Firt step: Select parrent categoy -->
    <div v-if="categories.length > 0 && currentStep === 1">
      <h2 class="mt-5" style="font-size: 34px">
        {{ 'support.contactForm.howCanWeHelpYou' | trans }}
      </h2>

      <p class="lead">
        {{ 'support.contactForm.pleaseSelectCategory' | trans }}
      </p>

      <div class="list-group support-list-group mt-4">
        <button
          type="button"
          class="list-group-item list-group-item-action"
          :class="{ active: category.id === currentParentCategory }"
          v-for="category in categories"
          @click.prevent="currentParentCategory = category"
        >
          <i class="wa-icon-long-arrow-right"></i>
          <i :class="category.icon" v-if="category.icon"></i>

          {{ category.name }}

          <small v-if="category.description">
            {{ category.description }}
          </small>
        </button>
      </div>
    </div>

    <!-- Second step: Select child categoy -->
    <div v-if="currentParentCategory !== null && currentStep === 2">
      <h2 class="mt-5" style="font-size: 34px">
        {{ 'support.contactForm.moreInfosPlease' | trans }}
      </h2>

      <p class="lead">
        {{ 'support.contactForm.pleaseSelectTopic' | trans }}
      </p>

      <div class="list-group support-list-group mt-4">
        <button
          type="button"
          class="list-group-item list-group-item-action"
          :class="{ active: category.id === currentChildCategory }"
          v-for="category in currentParentCategory.children"
          @click.prevent="currentChildCategory = category"
        >
          <i class="wa-icon-long-arrow-right"></i>
          <i :class="category.icon" v-if="category.icon"></i>

          {{ category.name }}

          <small v-if="category.description">
            {{ category.description }}
          </small>
        </button>
      </div>
    </div>

    <!-- Third step: Read help article -->
    <div v-if="currentChildCategory !== null && currentStep === 3">
      <h2 class="mt-5" style="font-size: 34px">
        {{ 'support.contactForm.fastSolutionsForYou' | trans }}
      </h2>

      <!-- Help articles loader -->
      <div class="text-center mt-4" v-if="isLoading === 'helpArticles'">
        <svg
          class="loader"
          viewBox="0 0 120 120"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle class="internal-circle" cx="60" cy="60" r="30"></circle>
          <circle class="external-circle" cx="60" cy="60" r="50"></circle>
        </svg>
      </div>

      <div class="list-group support-list-group mt-4">
        <a
          :href="
            helpArticleRoute.replace(
              'HELP-ARTICLE-PERMALINK',
              helpArticle.permalink
            )
          "
          target="_blank"
          class="list-group-item list-group-item-action"
          v-for="helpArticle in helpArticles"
        >
          <div class="media">
            <i class="fas fa-book-open align-self-center mr-3"></i>
            <div class="media-body">
              {{ helpArticle.title }}

              <small
                class="d-block text-muted"
                v-if="helpArticle.content.raw_content"
                style="line-height: 20px"
              >
                {{
                  strip(helpArticle.content.raw_content).substring(0, 150)
                }}...
              </small>
            </div>
          </div>
        </a>
      </div>
    </div>

    <!-- Fourth step: Contact form -->
    <div
      v-if="showContactForm === true && currentStep === 4 && !isSent"
      style="max-width: 800px; display: 0 auto"
    >
      <h2 class="mt-5" style="font-size: 34px">
        {{ 'support.contactForm.writeUsAMessage' | trans }}
      </h2>
      <p class="lead">
        {{ 'support.contactForm.moreInfosAreHelpful' | trans }}
      </p>

      <div class="alert alert-danger" v-if="Object.keys(formErrors).length > 0">
        <ul class="my-0" v-for="errorKey in Object.keys(formErrors)">
          <li v-for="error in formErrors[errorKey]">
            {{ error }}
          </li>
        </ul>
      </div>

      <!-- Fullname -->
      <div class="form-group mt-5">
        <label for="fullname">
          {{ 'support.contactForm.fullname' | trans }}
          <span class="required">*</span>
        </label>

        <input
          type="text"
          id="fullname"
          class="form-control"
          v-model="fullname"
          :placeholder="'support.contactForm.fullnamePlaceholder' | trans"
          :class="{ 'is-invalid': 'fullname' in formErrors }"
        />

        <div class="invalid-feedback" v-if="'fullname' in formErrors">
          {{ formErrors['fullname'][0] }}
        </div>
      </div>

      <!-- Email -->
      <div class="form-group mt-4">
        <label for="email">
          {{ 'support.contactForm.email' | trans }}
          <span class="required">*</span>
        </label>

        <input
          type="text"
          id="email"
          class="form-control"
          v-model="email"
          :placeholder="'support.contactForm.emailPlaceholder' | trans"
          :class="{ 'is-invalid': 'email' in formErrors }"
        />

        <div class="invalid-feedback" v-if="'email' in formErrors">
          {{ formErrors['email'][0] }}
        </div>
      </div>

      <!-- Booking number -->
      <div class="form-group mt-4">
        <label for="bookingNumber">
          {{ 'support.contactForm.bookingNumber' | trans }}
        </label>

        <input
          type="text"
          id="bookingNumber"
          class="form-control"
          v-model="bookingNumber"
          :placeholder="'support.contactForm.bookingNumberPlaceholder' | trans"
          :class="{ 'is-invalid': 'booking_number' in formErrors }"
        />

        <div class="invalid-feedback" v-if="'booking_number' in formErrors">
          {{ formErrors['booking_number'][0] }}
        </div>
      </div>

      <!-- Message -->
      <div class="form-group mt-4">
        <label for="message">
          {{ 'support.contactForm.message' | trans }}
          <span class="required">*</span>
        </label>

        <!-- Topic -->
        <div
          class="my-2"
          v-if="currentParentCategory !== null || currentChildCategory !== null"
        >
          <b>Thema:</b>
          <span v-if="currentParentCategory !== null">
            {{ currentParentCategory.name }}
          </span>

          <span
            v-if="
              currentParentCategory !== null && currentChildCategory !== null
            "
          >
            /
          </span>

          <span v-if="currentChildCategory !== null">
            {{ currentChildCategory.name }}
          </span>
        </div>

        <textarea
          id="message"
          class="form-control"
          v-model="message"
          :placeholder="'support.contactForm.messagePlaceholder' | trans"
          rows="5"
          :class="{ 'is-invalid': 'message' in formErrors }"
        ></textarea>

        <div class="invalid-feedback" v-if="'message' in formErrors">
          {{ formErrors['message'][0] }}
        </div>
      </div>

      <button
        type="button"
        class="btn btn-primary btn-lg btn-block mt-3"
        style="max-width: 300px"
        @click.prevent="send()"
        :disabled="isLoading"
      >
        <span v-if="!isLoading">
          {{ 'support.contactForm.submit' | trans }}
        </span>
        <span v-if="isLoading">
          <i class="fa fa-spinner fa-spin"></i>
        </span>
      </button>
    </div>

    <!-- Successfully sent -->
    <div
      class="alert alert-success mt-5 py-4"
      v-if="showContactForm === true && currentStep === 4 && isSent"
    >
      {{ 'support.contactForm.success' | trans({ email: email }) }}
    </div>

    <!-- Show contact form -->
    <button
      type="button"
      class="btn btn-primary btn-lg float-right mt-4 px-4 show-contact-form"
      @click.prevent="showContactForm = true"
      v-if="currentStep === 3"
    >
      {{ 'support.contactForm.showContactForm' | trans }}
      <i class="wa-icon-long-arrow-right ml-3"></i>
    </button>

    <!-- Step back -->
    <button
      type="button"
      class="btn btn-link btn-lg back-button mt-4"
      @click.prevent="stepBack()"
      v-if="currentStep > 1"
    >
      <i class="wa-icon-long-arrow-left"></i>
      {{ 'support.contactForm.back' | trans }}
    </button>
  </div>
</template>

<script>
export default {
  props: [
    'loadCategoriesEndpoint',
    'loadHelpArticlesEndpoint',
    'helpArticleRoute',

    // User data
    'userFullname',
    'userEmail',

    // Feedback
    'isFeedback',
  ],
  data: function () {
    return {
      categories: [],
      currentParentCategory: null,
      currentChildCategory: null,
      isError: false,
      isLoading: null,
      helpArticles: [],
      showContactForm: false,
      isSent: false,
      formErrors: {},

      // Steps
      totalSteps: 4,

      // Contact form fields
      fullname: null,
      email: null,
      bookingNumber: null,
      message: null,
    }
  },
  mounted: function () {
    this.loadCategories()

    // User data
    this.fullname = this.userFullname
    this.email = this.userEmail

    if (this.isFeedback) {
      this.showContactForm = true
    }
  },
  watch: {
    currentChildCategory: function () {
      this.loadHelpArticles()
    },

    currentStep: function () {
      $([document.documentElement, document.body]).animate(
        {
          scrollTop: $('.contact-form').offset().top - 30,
        },
        1000
      )
    },
  },
  computed: {
    currentStep: function () {
      if (this.isFeedback) return 4
      if (this.showContactForm) return 4
      if (this.currentChildCategory !== null) return 3
      if (this.currentParentCategory !== null) return 2

      return 1
    },

    subject: function () {
      var subject = ''
      if (
        this.currentParentCategory !== null ||
        this.currentChildCategory !== null
      ) {
        if (this.currentParentCategory !== null) {
          subject += this.currentParentCategory.name
        }

        if (
          this.currentParentCategory !== null &&
          this.currentChildCategory !== null
        ) {
          subject += ' / '
        }

        if (this.currentChildCategory !== null) {
          subject += this.currentChildCategory.name
        }
      }

      if (this.isFeedback) {
        return 'Feedback'
      }

      return subject
    },
  },
  methods: {
    loadCategories: function () {
      var self = this

      this.isLoading = 'categories'

      axios
        .get(this.loadCategoriesEndpoint, {})
        .then(function (response) {
          self.categories = response.data.data
          self.isLoading = null
        })
        .catch(function (error) {
          self.isError = true
          self.isLoading = null
        })
    },

    loadHelpArticles: function () {
      var self = this
      this.helpArticles = []

      this.isLoading = 'helpArticles'

      axios
        .get(
          this.loadHelpArticlesEndpoint.replace(
            'CATEGORY-ID',
            this.currentChildCategory.id
          ),
          {}
        )
        .then(function (response) {
          self.helpArticles = response.data.data
          self.isLoading = null
        })
        .catch(function (error) {
          self.isError = true
          self.isLoading = null
        })
    },

    stepBack: function () {
      // Step 2
      if (this.currentStep === 2) {
        this.currentParentCategory = null
      }

      // Step 3
      if (this.currentStep === 3) {
        this.currentChildCategory = null
      }

      // Step 4
      if (this.currentStep === 4) {
        this.showContactForm = false
      }
    },

    strip: function (html) {
      var tmp = document.createElement('DIV')
      tmp.innerHTML = html

      return tmp.textContent || tmp.innerText || ''
    },

    send: function () {
      var self = this

      this.isLoading = true

      axios
        .post(route('api.frontend.support.contactForm.send'), {
          fullname: this.fullname,
          email: this.email,
          booking_number: this.bookingNumber,
          subject: this.subject || 'Supportanfrage',
          message: this.message,
        })
        .then(function (response) {
          self.formErrors = {}

          if (response.data.success) {
            self.isSent = true
          }
          self.isLoading = false
        })
        .catch(function (error) {
          self.isLoading = false
          self.formErrors = error.response.data.errors
        })
    },
  },
}
</script>
